(function () {
  'use strict';

  const modal = document.querySelector('[data-quick-view-modal]');
  const qvPanel = modal?.querySelector('[data-quick-view-panel]');
  const qvContent = modal?.querySelector('[data-quick-view-content]');
  const qvLoading = modal?.querySelector('[data-quick-view-loading]');
  const productCardApi = window.ProductCard || {};
  const fetchCartState = productCardApi.fetchCartState || (() => Promise.resolve(null));
  const syncCartUI = productCardApi.syncCartUI || (() => Promise.resolve(null));
  const shopifyRoot = window.Shopify?.routes?.root || '/';

  if (!modal || !qvPanel || !qvContent || !qvLoading) return;

  let detachInteractions = null;
  let imageSwiper = null;

  function formatPrice(cents) {
    if (cents === null || cents === undefined) return '';

    const showCodes = window.__ornixSettings?.showCurrencyCodes;
    const format = showCodes
      ? (window.__ornixSettings?.moneyWithCurrencyFormat || '{{amount}}')
      : (window.__ornixSettings?.moneyFormat || '{{amount}}');

    if (window.Shopify?.formatMoney) {
      const result = Shopify.formatMoney(cents, format);
      if (!showCodes) {
        return result.replace(/\s*[A-Z]{3}\s*$/g, '').replace(/^\s*[A-Z]{3}\s*/g, '').trim();
      }
      return result;
    }

    const amount = cents / 100;
    const formatted = amount.toFixed(2);
    const withComma = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecimals = Math.floor(amount)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    let result = format
      .replace(/\{\{?\s*amount\s*\}?\}/g, withComma)
      .replace(/\{\{?\s*amount_no_decimals\s*\}?\}/g, noDecimals)
      .replace(/\{\{?\s*amount_with_comma_separator\s*\}?\}/g, formatted.replace('.', ','))
      .replace(/\{\{?\s*amount_no_decimals_with_comma_separator\s*\}?\}/g, noDecimals);

    if (!showCodes) {
      result = result.replace(/\s*[A-Z]{3}\s*$/g, '').replace(/^\s*[A-Z]{3}\s*/g, '');
    }

    return result.trim();
  }

  function openModal() {
    modal.setAttribute('aria-hidden', 'false');
    modal.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onModalKeydown);
    requestAnimationFrame(() => qvPanel.focus());
  }

  function closeModal() {
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onModalKeydown);
    qvContent.setAttribute('aria-hidden', 'true');
    if (typeof detachInteractions === 'function') {
      detachInteractions();
      detachInteractions = null;
    }
    qvLoading.style.display = 'none';
    resetQuickViewContent();
  }

  function onModalKeydown(event) {
    if (event.key === 'Escape') closeModal();
  }

  modal.querySelectorAll('[data-quick-view-close]').forEach((element) => {
    element.addEventListener('click', closeModal);
  });

  function stripHtml(html) {
    return String(html || '').replace(/<[^>]*>/g, '').trim();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getProductImageSrc(product) {
    if (product.featured_image) {
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image.url) return product.featured_image.url;
      if (product.featured_image.src) return product.featured_image.src;
    }

    const firstImage = product.images?.[0];
    if (!firstImage) return '';
    if (typeof firstImage === 'string') return firstImage;
    return firstImage.src || firstImage.url || '';
  }

  function getSecondaryImageSrc(product) {
    const secondImage = product.images?.[1];
    if (!secondImage) return '';
    if (typeof secondImage === 'string') return secondImage;
    return secondImage.src || secondImage.url || '';
  }

  function getProductGalleryImages(product, variant) {
    const sources = [];
    const variantSrc = getVariantImageSrc(variant);

    if (variantSrc) {
      sources.push({
        src: variantSrc,
        alt: product?.heading || '',
      });
    }

    (product?.images || []).forEach((image) => {
      if (!image) return;
      const src = typeof image === 'string' ? image : (image.src || image.url || '');
      if (!src) return;
      sources.push({
        src,
        alt: (typeof image === 'object' && image?.alt) || product?.heading || '',
      });
    });

    const uniqueImages = [];
    const seen = new Set();

    sources.forEach((image) => {
      if (!image.src || seen.has(image.src)) return;
      seen.add(image.src);
      uniqueImages.push(image);
    });

    return uniqueImages;
  }

  function getVariantImageSrc(variant) {
    if (!variant?.featured_image) return '';
    if (typeof variant.featured_image === 'string') return variant.featured_image;
    return variant.featured_image.src || variant.featured_image.url || '';
  }

  function getInitialVariant(product) {
    if (!Array.isArray(product?.variants) || product.variants.length === 0) return null;
    return product.variants.find((variant) => variant.available) || product.variants[0];
  }

  async function fetchProductPageState(handle) {
    const response = await fetch('/products/' + handle);
    if (!response.ok) throw new Error('Product page fetch failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const productScope = doc.querySelector('.product-page') || doc;

    return {
      vendor: productScope.querySelector('.vendor-name')?.textContent?.trim() || '',
      subtitle: productScope.querySelector('.product-subtitle')?.textContent?.trim() || '',
      sellingPlanHtml: productScope.querySelector('.selling-plan-wrapper')?.innerHTML || '',
      giftCardHtml: productScope.querySelector('#gift-card-recipient')?.outerHTML || '',
    };
  }

  function getOptionDefinitions(product) {
    if (!product || !Array.isArray(product.options)) return [];

    return product.options.map((option, index) => {
      const optionKey = 'option' + (index + 1);
      const optionName = typeof option === 'string'
        ? option
        : String(option?.name || '').trim();
      const optionValues = Array.isArray(option?.values)
        ? option.values.map((value) => String(value))
        : [];
      const variantValues = (product.variants || [])
        .map((variant) => variant[optionKey])
        .filter(Boolean)
        .map((value) => String(value));
      const values = [...new Set(optionValues.concat(variantValues))];

      return {
        index,
        key: optionKey,
        name: optionName,
        values,
      };
    }).filter((definition) => {
      if (!definition.name || definition.values.length === 0) return false;

      const normalizedName = definition.name.toLowerCase();
      const normalizedValues = definition.values.map((value) => value.toLowerCase());
      const isDefaultOnlyOption = normalizedName === 'title'
        && normalizedValues.length === 1
        && normalizedValues[0] === 'default title';

      return !isDefaultOnlyOption;
    });
  }

  function isColorOption(name) {
    const normalized = String(name || '').trim().toLowerCase();
    return normalized.includes('color') || normalized.includes('colour');
  }

  function getSelectedVariant(product, selections) {
    if (!Array.isArray(product?.variants) || product.variants.length === 0) return null;

    return product.variants.find((variant) => {
      return Object.keys(selections).every((key) => variant[key] === selections[key]);
    }) || getInitialVariant(product);
  }

  function setButtonState(button, disabled, text, variantId) {
    if (!button) return;
    button.disabled = !!disabled;
    button.textContent = text;
    if (variantId) button.dataset.variantId = String(variantId);
    else delete button.dataset.variantId;
  }

  function destroyImageSwiper() {
    if (imageSwiper && typeof imageSwiper.destroy === 'function') {
      imageSwiper.destroy(true, true);
    }
    imageSwiper = null;
  }

  function initImageSwiper(imageCount) {
    const swiperEl = qvContent.querySelector('[data-qv-image-swiper]');
    const paginationEl = qvContent.querySelector('[data-qv-image-pagination]');

    destroyImageSwiper();

    if (!swiperEl || typeof Swiper === 'undefined' || imageCount <= 1) return;

    imageSwiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      pagination: paginationEl
        ? {
            el: paginationEl,
            clickable: true,
          }
        : undefined,
    });
  }

  function scopeGiftCardFields(container) {
    if (!container) return;

    const prefix = 'qv-' + Date.now();
    const fields = [
      'gift-card-recipient',
      'recipient-toggle',
      'recipient-fields',
      'recipient-email',
      'recipient-name',
      'recipient-message',
      'send-on',
    ];

    fields.forEach((id) => {
      const element = container.querySelector('#' + id);
      if (!element) return;
      const scopedId = prefix + '-' + id;
      container.querySelectorAll('label[for="' + id + '"]').forEach((label) => {
        label.setAttribute('for', scopedId);
      });
      element.id = scopedId;
    });
  }

  function initGiftCardRecipient(container) {
    if (!container) return;

    scopeGiftCardFields(container);

    const toggle = container.querySelector('.recipient-checkbox');
    const fields = container.querySelector('.recipient-fields');
    if (!toggle || !fields) return;

    fields.style.display = toggle.checked ? 'flex' : 'none';
    toggle.addEventListener('change', function () {
      fields.style.display = this.checked ? 'flex' : 'none';
    });
  }

  function initSellingPlanInputs(container) {
    if (!container) return;

    container.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
      input.name = 'qv_selling_plan';
    });

    const checkedInput = container.querySelector('input[name="qv_selling_plan"][checked]');
    if (!container.querySelector('input[name="qv_selling_plan"]:checked')) {
      if (checkedInput) checkedInput.checked = true;
      else {
        const firstInput = container.querySelector('input[name="qv_selling_plan"]');
        if (firstInput) firstInput.checked = true;
      }
    }
  }

  function renderSupplementaryContent(pageState) {
    const vendorElement = qvContent.querySelector('[data-qv-vendor]');
    const subtitleElement = qvContent.querySelector('[data-qv-subtitle]');
    const sellingPlansElement = qvContent.querySelector('[data-qv-selling-plans]');
    const giftCardElement = qvContent.querySelector('[data-qv-gift-card]');

    if (vendorElement) {
      vendorElement.textContent = pageState?.vendor || '';
      vendorElement.hidden = !pageState?.vendor;
    }

    if (subtitleElement) {
      subtitleElement.textContent = pageState?.subtitle || '';
      subtitleElement.hidden = !pageState?.subtitle;
    }

    if (sellingPlansElement) {
      sellingPlansElement.innerHTML = pageState?.sellingPlanHtml || '';
      sellingPlansElement.hidden = !pageState?.sellingPlanHtml;
      if (pageState?.sellingPlanHtml) initSellingPlanInputs(sellingPlansElement);
    }

    if (giftCardElement) {
      giftCardElement.innerHTML = pageState?.giftCardHtml || '';
      giftCardElement.hidden = !pageState?.giftCardHtml;
      if (pageState?.giftCardHtml) initGiftCardRecipient(giftCardElement);
    }
  }

  function updateAvailability(variant) {
    const availabilityElement = qvContent.querySelector('[data-qv-availability]');
    if (!availabilityElement || !variant) return;

    const inStockLabel = availabilityElement.dataset.inStockLabel || 'In stock';
    const soldOutLabel = availabilityElement.dataset.soldOutLabel || 'Sold out';

    availabilityElement.textContent = variant.available ? inStockLabel : soldOutLabel;
    availabilityElement.hidden = false;
  }

  function getSelectedSellingPlan() {
    const selectedInput = qvContent.querySelector('input[name="qv_selling_plan"]:checked');
    if (!selectedInput) return '';
    return selectedInput.value || '';
  }

  function getGiftCardProperties() {
    const giftCardContainer = qvContent.querySelector('[data-qv-gift-card]');
    if (!giftCardContainer || giftCardContainer.hidden) return {};

    const toggle = giftCardContainer.querySelector('.recipient-checkbox');
    if (toggle && !toggle.checked) return {};

    const properties = {};
    giftCardContainer.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]').forEach((field) => {
      if (!field.name) return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
      const key = field.name.replace('properties[', '').replace(']', '');
      if (!key) return;
      properties[key] = field.value;
    });

    return properties;
  }

  function updateQuickViewPrice(variant) {
    const priceRegular = qvContent.querySelector('[data-qv-price-regular]');
    const priceSale = qvContent.querySelector('[data-qv-price-sale]');
    const priceCompare = qvContent.querySelector('[data-qv-price-compare]');

    if (!variant) {
      if (priceRegular) priceRegular.textContent = '';
      if (priceSale) priceSale.hidden = true;
      if (priceCompare) priceCompare.hidden = true;
      return;
    }

    const onSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
    if (onSale) {
      if (priceRegular) priceRegular.hidden = true;

      if (priceSale) {
        priceSale.textContent = formatPrice(variant.price);
        priceSale.hidden = false;
      }

      if (priceCompare) {
        priceCompare.textContent = formatPrice(variant.compare_at_price);
        priceCompare.hidden = false;
      }

      return;
    }

    if (priceRegular) {
      priceRegular.textContent = formatPrice(variant.price);
      priceRegular.hidden = false;
    }

    if (priceSale) priceSale.hidden = true;
    if (priceCompare) priceCompare.hidden = true;
  }

  function updateBadges(product, variant) {
    const settings = window.__ornixSettings || {};
    const badgeShape = settings.badge_shape
    const badgeSize = settings.badge_text_size
    const badgeWeight = settings.badge_font_weight

    const applyBadgeStyles = (element, bg, color) => {
      if (!element) return;
      element.className = `badge badge--${badgeShape} badge--${badgeSize}`;
      element.style.background = bg;
      element.style.color = color;
      element.style.fontWeight = badgeWeight;
    };

    const soldOutBadge = qvContent.querySelector('[data-qv-badge-sold-out]');
    const saleBadge = qvContent.querySelector('[data-qv-badge-sale]');
    const newBadge = qvContent.querySelector('[data-qv-badge-new]');
    const isAvailable = !!variant?.available;
    const compareAt = Number(variant?.compare_at_price || 0);
    const price = Number(variant?.price || 0);
    const isOnSale = compareAt > price;
    const isNew = Array.isArray(product.tags) && product.tags.some((tag) => String(tag).toLowerCase() === 'new');

    if (soldOutBadge) {
      soldOutBadge.hidden = isAvailable;
      if (!isAvailable) {
        soldOutBadge.textContent = 'Sold Out';
        applyBadgeStyles(
          soldOutBadge,
          settings.badge_sold_out_bg,
          settings.badge_sold_out_text
        );
      }
    }

    if (saleBadge) {
      saleBadge.hidden = !isOnSale;
      if (isOnSale) {
        const percentOff = Math.round(((compareAt - price) / compareAt) * 100);
        saleBadge.textContent = percentOff + '% OFF';
        applyBadgeStyles(
          saleBadge,
          settings.badge_sale_bg,
          settings.badge_sale_text
        );
      }
    }

    if (newBadge) {
      newBadge.hidden = !isNew;
      if (isNew) {
        newBadge.textContent = 'New';
        applyBadgeStyles(
          newBadge,
          settings.badge_new_bg,
          settings.badge_new_text
        );
      }
    }
  }

  function renderOptionGroups(optionDefinitions, selections, product) {
    const optionsContainer = qvContent.querySelector('[data-qv-options]');
    if (!optionsContainer) return;

    if (!optionDefinitions.length) {
      optionsContainer.innerHTML = '';
      optionsContainer.hidden = true;
      return;
    }

    optionsContainer.innerHTML = optionDefinitions.map((definition) => {
      const optionName = escapeHtml(definition.name);
      const selectedValue = escapeHtml(selections[definition.key] || '');
      const controlsClass = isColorOption(definition.name) ? 'qv-colors' : 'qv-sizes';

      const controls = definition.values.map((value) => {
        const escapedValue = escapeHtml(value);
        const isActive = selections[definition.key] === value;

        if (isColorOption(definition.name)) {
          const matchedVariant = product.variants.find((variant) => variant[definition.key] === value);
          const variantImage = escapeHtml(getVariantImageSrc(matchedVariant));
          const swatchColor = escapeHtml(String(value).toLowerCase().replace(/\s+/g, '-'));

          return `<button
            type="button"
            class="qv-color-swatch${isActive ? ' qv-color-swatch--active' : ''}"
            data-qv-option-index="${definition.index}"
            data-qv-option-value="${escapedValue}"
            data-qv-option-image="${variantImage}"
            style="background-color: ${swatchColor};"
            heading="${escapedValue}"
            aria-label="${optionName}: ${escapedValue}"
            aria-pressed="${isActive ? 'true' : 'false'}"
          ></button>`;
        }

        return `<button
          type="button"
          class="qv-size-btn${isActive ? ' qv-size-btn--active' : ''}"
          data-qv-option-index="${definition.index}"
          data-qv-option-value="${escapedValue}"
          aria-pressed="${isActive ? 'true' : 'false'}"
        >${escapedValue}</button>`;
      }).join('');

      return `<div class="qv-option">
        <p class="qv-option-label">${optionName}: <span>${selectedValue}</span></p>
        <div class="${controlsClass}">
          ${controls}
        </div>
      </div>`;
    }).join('');

    optionsContainer.hidden = false;
  }

  function updateImages(product, variant) {
    const slidesWrap = qvContent.querySelector('[data-qv-image-slides]');
    const paginationEl = qvContent.querySelector('[data-qv-image-pagination]');
    const imageWrap = qvContent.querySelector('[data-qv-image-wrap]');
    const images = getProductGalleryImages(product, variant);

    if (!slidesWrap) return;

    slidesWrap.innerHTML = images.map((image) => {
      return `<div class="swiper-slide qv-image__slide">
        <img
          src="${escapeHtml(image.src)}"
          alt="${escapeHtml(image.alt || product.title || '')}"
          loading="lazy"
          width="1000"
          height="1000"
        >
      </div>`;
    }).join('');

    if (paginationEl) paginationEl.hidden = images.length <= 1;
    if (imageWrap) {
      imageWrap.classList.toggle('qv-image--has-secondary', images.length > 1);
    }

    initImageSwiper(images.length);
  }

  function updateStaticContent(product, variant, selections, optionDefinitions, pageState) {
    const titleElement = qvContent.querySelector('[data-qv-heading]');
    const atcButton = qvContent.querySelector('[data-qv-atc]');
    const buyButton = qvContent.querySelector('[data-qv-buy]');
    const quantityInput = qvContent.querySelector('[data-qv-qty]');
    const viewLink = qvContent.querySelector('[data-qv-view-link]');
    const facebookButton = qvContent.querySelector('[data-qv-share-facebook]');
    const twitterButton = qvContent.querySelector('[data-qv-share-twitter]');
    const pinterestButton = qvContent.querySelector('[data-qv-share-pinterest]');
    const skuElement = qvContent.querySelector('[data-qv-sku]');
    const productUrl = encodeURIComponent(window.location.origin + '/products/' + product.handle);
    const productTitle = encodeURIComponent(product.title || '');

    if (titleElement) titleElement.textContent = product.title || '';

    updateImages(product, variant);
    updateBadges(product, variant);
    updateQuickViewPrice(variant);
    updateAvailability(variant);
    renderOptionGroups(optionDefinitions, selections, product);
    renderSupplementaryContent(pageState);

    if (quantityInput) {
      quantityInput.value = Math.max(1, parseInt(quantityInput.value, 10) || 1);
    }

    setButtonState(atcButton, !variant?.available, variant?.available ? 'Add to Cart' : 'Sold Out', variant?.id);
    setButtonState(buyButton, !variant?.available, variant?.available ? 'Buy Now' : 'Unavailable', variant?.id);

    if (skuElement) {
      if (variant?.sku) {
        skuElement.textContent = 'SKU: ' + variant.sku;
        skuElement.style.display = 'block';
      } else {
        skuElement.textContent = '';
        skuElement.style.display = 'none';
      }
    }

    if (viewLink) viewLink.href = '/products/' + product.handle;
    if (facebookButton) facebookButton.href = `https://www.facebook.com/sharer/sharer.php?u=${productUrl}`;
    if (twitterButton) twitterButton.href = `https://x.com/intent/tweet?url=${productUrl}&text=${productTitle}`;
    if (pinterestButton) pinterestButton.href = `https://pinterest.com/pin/create/button/?url=${productUrl}&description=${productTitle}`;
  }

  function resetQuickViewContent() {
    const titleElement = qvContent.querySelector('[data-qv-heading]');
    const slidesWrap = qvContent.querySelector('[data-qv-image-slides]');
    const paginationEl = qvContent.querySelector('[data-qv-image-pagination]');
    const imageWrap = qvContent.querySelector('[data-qv-image-wrap]');
    const optionsContainer = qvContent.querySelector('[data-qv-options]');
    const quantityInput = qvContent.querySelector('[data-qv-qty]');
    const priceRegular = qvContent.querySelector('[data-qv-price-regular]');
    const priceSale = qvContent.querySelector('[data-qv-price-sale]');
    const priceCompare = qvContent.querySelector('[data-qv-price-compare]');
    const atcButton = qvContent.querySelector('[data-qv-atc]');
    const buyButton = qvContent.querySelector('[data-qv-buy]');
    const viewLink = qvContent.querySelector('[data-qv-view-link]');
    const facebookButton = qvContent.querySelector('[data-qv-share-facebook]');
    const twitterButton = qvContent.querySelector('[data-qv-share-twitter]');
    const pinterestButton = qvContent.querySelector('[data-qv-share-pinterest]');
    const soldOutBadge = qvContent.querySelector('[data-qv-badge-sold-out]');
    const saleBadge = qvContent.querySelector('[data-qv-badge-sale]');
    const newBadge = qvContent.querySelector('[data-qv-badge-new]');
    const vendorElement = qvContent.querySelector('[data-qv-vendor]');
    const subtitleElement = qvContent.querySelector('[data-qv-subtitle]');
    const availabilityElement = qvContent.querySelector('[data-qv-availability]');
    const sellingPlansElement = qvContent.querySelector('[data-qv-selling-plans]');
    const giftCardElement = qvContent.querySelector('[data-qv-gift-card]');

    if (titleElement) titleElement.textContent = '';
    if (vendorElement) {
      vendorElement.textContent = '';
      vendorElement.hidden = true;
    }
    if (subtitleElement) {
      subtitleElement.textContent = '';
      subtitleElement.hidden = true;
    }
    if (availabilityElement) {
      availabilityElement.textContent = '';
      availabilityElement.hidden = true;
    }

    destroyImageSwiper();
    if (slidesWrap) slidesWrap.innerHTML = '';
    if (paginationEl) paginationEl.hidden = true;
    if (imageWrap) imageWrap.classList.remove('qv-image--has-secondary');

    if (optionsContainer) {
      optionsContainer.innerHTML = '';
      optionsContainer.hidden = true;
    }
    if (sellingPlansElement) {
      sellingPlansElement.innerHTML = '';
      sellingPlansElement.hidden = true;
    }
    if (giftCardElement) {
      giftCardElement.innerHTML = '';
      giftCardElement.hidden = true;
    }

    if (quantityInput) quantityInput.value = '1';

    if (priceRegular) {
      priceRegular.textContent = '';
      priceRegular.hidden = false;
    }
    if (priceSale) {
      priceSale.textContent = '';
      priceSale.hidden = true;
    }
    if (priceCompare) {
      priceCompare.textContent = '';
      priceCompare.hidden = true;
    }

    setButtonState(atcButton, false, 'Add to Cart');
    setButtonState(buyButton, false, 'Buy Now');

    if (viewLink) viewLink.href = '#';
    if (facebookButton) facebookButton.href = '#';
    if (twitterButton) twitterButton.href = '#';
    if (pinterestButton) pinterestButton.href = '#';

    if (soldOutBadge) soldOutBadge.hidden = true;
    if (saleBadge) {
      saleBadge.hidden = true;
      saleBadge.textContent = '';
    }
    if (newBadge) newBadge.hidden = true;
  }

  function initQuickViewInteractions(product, optionDefinitions, selections, pageState) {
    if (typeof detachInteractions === 'function') detachInteractions();

    const controller = new AbortController();
    const signal = controller.signal;
    const quantityInput = qvContent.querySelector('[data-qv-qty]');
    const atcButton = qvContent.querySelector('[data-qv-atc]');
    const buyButton = qvContent.querySelector('[data-qv-buy]');

    function currentVariant() {
      return getSelectedVariant(product, selections);
    }

    function refreshView() {
      updateStaticContent(product, currentVariant(), selections, optionDefinitions, pageState);
    }

    qvContent.addEventListener('click', (event) => {
      const optionButton = event.target.closest('[data-qv-option-index][data-qv-option-value]');
      if (optionButton) {
        const index = Number(optionButton.dataset.qvOptionIndex);
        const definition = optionDefinitions[index];
        if (!definition) return;
        selections[definition.key] = optionButton.dataset.qvOptionValue;
        refreshView();
        return;
      }

      if (event.target.closest('[data-qv-qty-minus]')) {
        if (!quantityInput) return;
        const current = Math.max(1, parseInt(quantityInput.value, 10) || 1);
        quantityInput.value = String(Math.max(1, current - 1));
        return;
      }

      if (event.target.closest('[data-qv-qty-plus]')) {
        if (!quantityInput) return;
        const current = Math.max(1, parseInt(quantityInput.value, 10) || 1);
        quantityInput.value = String(Math.min(99, current + 1));
      }
    }, { signal });

    quantityInput?.addEventListener('input', () => {
      const numericValue = Math.max(1, parseInt(quantityInput.value, 10) || 1);
      quantityInput.value = String(numericValue);
    }, { signal });

    buyButton?.addEventListener('click', async () => {
      const variant = currentVariant();
      const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
      const sellingPlan = getSelectedSellingPlan();
      const properties = getGiftCardProperties();
      if (!variant?.id || !variant.available || buyButton.disabled) return;

      setButtonState(buyButton, true, 'Redirecting...', variant.id);
      try {
        const response = await fetch(shopifyRoot + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: variant.id,
            quantity,
            ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
            ...(Object.keys(properties).length ? { properties } : {}),
          }),
        });

        if (!response.ok) throw new Error('Buy now failed');
        window.location.href = shopifyRoot + 'checkout';
      } catch (error) {
        console.error('[QuickView BuyNow]', error);
        refreshView();
      }
    }, { signal });

    atcButton?.addEventListener('click', async () => {
      const variant = currentVariant();
      const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
      const sellingPlan = getSelectedSellingPlan();
      const properties = getGiftCardProperties();
      if (!variant?.id || !variant.available || atcButton.disabled) return;

      setButtonState(atcButton, true, 'Adding...', variant.id);
      try {
        const response = await fetch(shopifyRoot + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: variant.id,
            quantity,
            ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
            ...(Object.keys(properties).length ? { properties } : {}),
          }),
        });

        if (!response.ok) throw new Error('Add to cart failed');

        await response.json();
        const cart = await fetchCartState();
        const cartDrawer = await syncCartUI(cart);
        setButtonState(atcButton, true, 'Added!', variant.id);
        closeModal();

        const isDrawerCart = !window.__ornixSettings?.cart_type || window.__ornixSettings.cart_type === 'drawer';
        if (isDrawerCart && cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      } catch (error) {
        console.error('[QuickView ATC]', error);
        refreshView();
      }
    }, { signal });

    detachInteractions = () => controller.abort();
  }

  async function loadQuickView(handle) {
    resetQuickViewContent();
    qvContent.setAttribute('aria-hidden', 'true');
    qvLoading.style.display = 'flex';
    openModal();

    try {
      const [productResponse, pageState] = await Promise.all([
        fetch('/products/' + handle + '.js'),
        fetchProductPageState(handle),
      ]);
      if (!productResponse.ok) throw new Error('Product fetch failed');

      const product = await productResponse.json();
      const optionDefinitions = getOptionDefinitions(product);
      const initialVariant = getInitialVariant(product);
      if (!initialVariant) throw new Error('Product has no variants');

      const selections = {};
      optionDefinitions.forEach((definition) => {
        selections[definition.key] = initialVariant[definition.key] || definition.values[0];
      });

      updateStaticContent(product, initialVariant, selections, optionDefinitions, pageState);
      initQuickViewInteractions(product, optionDefinitions, selections, pageState);

      qvLoading.style.display = 'none';
      qvContent.setAttribute('aria-hidden', 'false');
    } catch (error) {
      console.error('[QuickView]', error);
      qvLoading.style.display = 'none';

      const titleElement = qvContent.querySelector('[data-qv-heading]');
      if (titleElement) titleElement.textContent = 'Quick view unavailable';
      qvContent.setAttribute('aria-hidden', 'false');
    }
  }

  function trapFocus(container) {
    container.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;

      const focusable = [...container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => !element.hasAttribute('hidden'));

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  trapFocus(qvPanel);

  function initQuickViewTriggers() {
    document.querySelectorAll('[data-quick-view-trigger]').forEach((button) => {
      if (button._quickViewBound) return;
      button._quickViewBound = true;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        const handle = button.dataset.productHandle;
       if (handle) window.location.href = shopifyRoot + 'products/' + encodeURIComponent(handle);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuickViewTriggers);
  } else {
    initQuickViewTriggers();
  }
})();
